"use client";

import { c as _c } from "react/compiler-runtime";
import { cva } from "class-variance-authority";
import { buttonVariants } from "fumadocs-ui/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "fumadocs-ui/components/ui/popover";
import { useCopyButton } from "fumadocs-ui/utils/use-copy-button";
import { Check, ChevronDown, Copy, ExternalLinkIcon } from "lucide-react";
import { useMemo, useState } from "react";

// import { Logo } from "@/components/logo";
import { getChatUrl } from "@/lib/branding";
import { cn } from "@/lib/cn";
import { useDocsI18n } from "@/lib/i18n";
import { Logo } from "../logo";
const cache = new Map<string, string>();
export function LLMCopyButton({
  /**
   * A URL to fetch the raw Markdown/MDX content of page
   */
  markdownUrl
}: {
  markdownUrl: string;
}) {
  const {
    t
  } = useDocsI18n();
  const [isLoading, setIsLoading] = useState(false);
  const [checked, onClick] = useCopyButton(async () => {
    const cached = cache.get(markdownUrl);
    if (cached) {
      await navigator.clipboard.writeText(cached);
      return;
    }
    setIsLoading(true);
    try {
      await navigator.clipboard.write([new ClipboardItem({
        "text/plain": fetch(markdownUrl).then(async res => {
          const content = await res.text();
          cache.set(markdownUrl, content);
          return content;
        })
      })]);
    } finally {
      setIsLoading(false);
    }
  });
  return <button disabled={isLoading} className={cn(buttonVariants({
    color: "secondary",
    size: "sm",
    className: "gap-2 [&_svg]:size-3.5 [&_svg]:text-fd-muted-foreground"
  }))} onClick={onClick}>
			{checked ? <Check /> : <Copy />}
			{t("pageActions.copyMarkdown")}
		</button>;
}
const optionVariants = cva("text-sm p-2 rounded-lg inline-flex items-center gap-2 hover:text-fd-accent-foreground hover:bg-fd-accent [&_svg]:size-4");
export function ViewOptions(t0) {
  const $ = _c(41);
  const {
    markdownUrl
  } = t0;
  const {
    t
  } = useDocsI18n();
  let t1;
  if ($[0] !== markdownUrl) {
    t1 = typeof window !== "undefined" ? new URL(markdownUrl, window.location.origin) : "loading";
    $[0] = markdownUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const fullMarkdownUrl = t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[2] !== fullMarkdownUrl || $[3] !== t) {
    const q = t("pageActions.askPrompt", {
      url: String(fullMarkdownUrl)
    });
    let t6;
    if ($[8] !== t) {
      t6 = t("pageActions.openInChatGPT");
      $[8] = t;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    const t7 = `https://chatgpt.com/?${new URLSearchParams({
      hints: "search",
      q
    })}`;
    let t8;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <svg role="img" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><title>OpenAI</title><path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" /></svg>;
      $[10] = t8;
    } else {
      t8 = $[10];
    }
    if ($[11] !== t6 || $[12] !== t7) {
      t4 = {
        title: t6,
        href: t7,
        icon: t8
      };
      $[11] = t6;
      $[12] = t7;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    let t9;
    if ($[14] !== t) {
      t9 = t("pageActions.openInClaude");
      $[14] = t;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    const t10 = `https://claude.ai/new?${new URLSearchParams({
      q
    })}`;
    let t11;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <svg fill="currentColor" role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><title>Anthropic</title><path d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z" /></svg>;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    if ($[17] !== t10 || $[18] !== t9) {
      t5 = {
        title: t9,
        href: t10,
        icon: t11
      };
      $[17] = t10;
      $[18] = t9;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    if ($[20] !== t) {
      t2 = t("pageActions.openInLLMGateway");
      $[20] = t;
      $[21] = t2;
    } else {
      t2 = $[21];
    }
    t3 = process.env.NODE_ENV === "development" ? `http://localhost:3003?${new URLSearchParams({
      q
    })}&hints=search&model=google-ai-studio/gemini-3-flash-preview` : `${getChatUrl()}?${new URLSearchParams({
      q
    })}&hints=search&model=google-ai-studio/gemini-3-flash-preview`;
    $[2] = fullMarkdownUrl;
    $[3] = t;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
  }
  let t6;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Logo />;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== t2 || $[24] !== t3) {
    t7 = {
      title: t2,
      href: t3,
      icon: t6
    };
    $[23] = t2;
    $[24] = t3;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== t4 || $[27] !== t5 || $[28] !== t7) {
    t8 = [t4, t5, t7];
    $[26] = t4;
    $[27] = t5;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const items = t8;
  let t9;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn(buttonVariants({
      color: "secondary",
      size: "sm",
      className: "gap-2"
    }));
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== t) {
    t10 = t("pageActions.open");
    $[31] = t;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ChevronDown className="size-3.5 text-fd-muted-foreground" />;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10) {
    t12 = <PopoverTrigger className={t9}>{t10}{t11}</PopoverTrigger>;
    $[34] = t10;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== items) {
    t13 = <PopoverContent className="flex flex-col overflow-auto">{items.map(_temp)}</PopoverContent>;
    $[36] = items;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t12 || $[39] !== t13) {
    t14 = <Popover>{t12}{t13}</Popover>;
    $[38] = t12;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  return t14;
}
function _temp(item) {
  return <a key={item.href} href={item.href} rel="noreferrer noopener" target="_blank" className={cn(optionVariants())}>{item.icon}{item.title}<ExternalLinkIcon className="text-fd-muted-foreground size-3.5 ms-auto" /></a>;
}